import {
  FedTypes,
  formatFed,
} from '../components/FederalIdInput/FederalIdInput';
import { apiClient, ApiCallOptions } from './apiClient';
import { resolveIsFinalAssignment } from './assignments';
import {
  ClientRelationship,
  EditedClient,
  NewClient,
  OWNERSHIP_RELATIONSHIP_TYPE_IDS,
  RELATIONSHIP_CATEGORIES,
} from './relationships';

export type ClientResponse = {
  accountId: { id: number; name: string } | null;
  additionalInformation: string | undefined;
  billingType: { id: number; name: string } | null;
  businessActivity: { id: number; name: string } | null;
  closeDate: string | undefined;
  createdAt: string;
  dateEstablished: string;
  deletedAt: string | null;
  domesticState: { id: number; name: string };
  entityType: { id: number; name: string };
  entityTypeHistory: {
    id: number;
    startingDate: string;
    endingDate: string;
    entityTypeId: number;
    description: string;
    createdAt: string;
    createdBy: {
      fullName: string;
      id: number;
      username: string;
    } | null;
  }[];
  federalLegalId: string;
  filingCategory: { id: number; name: string } | null;
  filingStatus: { id: number; name: string };
  filingStatusHistory: {
    id: number;
    startingDate: string;
    endingDate: string;
    filingStatusId: number;
    description: string;
    createdAt: string;
    createdBy: {
      fullName: string;
      id: number;
      username: string;
    } | null;
  }[];
  id: number;
  isActive: boolean;
  isClient: boolean;
  isFormedByUs: boolean;
  isLocked: boolean;
  name: string;
  parent: { id: number; name: string } | null;
  payrollProvider: string;
  templates: {
    id: number;
    templateInfo: {
      templateId: number;
      templateName: string;
      state: { id: number; name: string } | null;
      templateType: { id: number; name: string } | null;
      filesUnder: { id: number; name: string } | null;
      taxPayment: { id: number; name: string } | null;
      mainFiling: { id: number; name: string } | null;
      recurrenceFrequency: { id: number; name: string } | null;
      templateRecurrenceId: number;
      startingDate: string;
      endingDate?: string;
      isFinal?: boolean;
      defaultStaffId: number;
      quickReminder?: string;
      totalUsedTasks?: number;
    };
    billingAndPayments: {
      isSeparatelyCharged: boolean;
      amount?: number;
      estimateAmount?: number;
      isSubjectToCommission: boolean;
      commissionRate?: number;
      priceStatus?: number;
      isPriceChanged: boolean;
      newPrice?: number;
      paymentType?: number;
      dayOfMonth?: number;
      defaultPaymentMethod?:
        | { bank: Omit<ClientResponse['bankAccounts'][number], 'id'> }
        | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> }
        | null;
      customerContact?:
        | number
        | Exclude<ClientResponse['contacts'][number], undefined>;
    };
    recurrence: {
      recurrenceOverride: {
        firstMonth?: number;
        secondMonth?: number;
        thirdMonth?: number;
        fourthMonth?: number;
        dayOfMonth?: number;
      } | null;
    };
    assignments: {
      id: number;
      templateAssignmentId: number | null;
      name: string;
      assignmentTypeId: number;
      defaultStaffId: number;
      order: number;
      isRequired: boolean;
      canBeDeleted: boolean;
      isActive: boolean;
      isFinal: boolean;
      amount: number | null;
      totalUsedTasks: number;
    }[];
    states?: {
      added: {
        id?: number;
        templateStateId: number;
        clientStateId:
          | number
          | Exclude<ClientResponse['stateHistory'][number], undefined>;
        name: string;
        form: string | null;
        state: {
          id: number;
          name: string;
        };
        startingDate: string | null;
        endingDate: string | null;
        isFinal?: boolean;
        quickReminder: string | null;
        paymentMethod:
          | { bank: Omit<ClientResponse['bankAccounts'][number], 'id'> }
          | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> }
          | null;
        totalUsedTasks: number;
      }[];
      available: {
        id?: number;
        templateStateId: number;
        clientStateId:
          | number
          | Exclude<ClientResponse['stateHistory'][number], undefined>;
        name: string;
        form: string | null;
        state: {
          id: number;
          name: string;
        };
        startingDate: string | null;
        endingDate: string | null;
        isFinal?: boolean;
        quickReminder: string | null;
        paymentMethod:
          | { bank: Omit<ClientResponse['bankAccounts'][number], 'id'> }
          | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> }
          | null;
        totalUsedTasks: number;
      }[];
      outOfRange: {
        id?: number;
        templateStateId: number;
        clientStateId:
          | number
          | Exclude<ClientResponse['stateHistory'][number], undefined>;
        name: string;
        form: string | null;
        state: {
          id: number;
          name: string;
        };
        startingDate: string | null;
        endingDate: string | null;
        isFinal?: boolean;
        quickReminder: string | null;
        paymentMethod:
          | { bank: Omit<ClientResponse['bankAccounts'][number], 'id'> }
          | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> }
          | null;
        totalUsedTasks: number;
      }[];
    };
  }[];
  stateHistory: {
    id: number;
    description: string;
    endingDate: string | null;
    governmentId: string;
    startingDate: string;
    stateId: number;
    stateResidenceId: number;
    createdAt: string;
    createdBy: {
      fullName: string;
      id: number;
      username: string;
    } | null;
  }[];
  contacts: {
    ccEmails: string[];
    cell: string | null;
    contactRoles: number[];
    contactTypeId: number;
    dob: string | null;
    email: string | null;
    fax: string | null;
    firstName: string;
    id: number;
    lastName: string;
    phone: string | null;
    ssn: string | null;
    startingDate: string | null;
  }[];
  addresses: {
    id: number;
    city?: {
      id: number;
      name: string;
    };
    contactRoles: number[];
    contactTypeId: number;
    createdBy?: {
      id: number;
      name: string;
    };
    startingDate: string | null;
    state?: {
      id: number;
      name: string;
    };
    street: string;
    unit: string | null;
    zipCode: {
      id: number;
      name: string;
    };
  }[];
  outsideProviders: {
    contact: {
      id: number;
      phone: string | null;
      cell: string | null;
      ccEmails: string[] | null;
      email: string | null;
      contactPerson: string;
    };
    endingDate: string | null;
    id: number;
    name: string;
    startingDate: string;
    type: {
      id: number;
      name: string;
    };
  }[];
  bankAccounts: {
    accountNumber: string;
    description: string;
    id: number;
    isDefaultPaymentMethod: boolean;
    routingNumber: string;
  }[];
  creditCards: {
    accountNumber: string;
    address: string;
    description: string;
    expirationDate: string;
    id: number;
    isDefaultPaymentMethod: boolean;
    nameOnAccount: string;
    securityCode: string;
  }[];
  commissionDetails: {
    id?: number;
    clientId?: number;
    commissionRate?: number;
    isSubjectToCommission?: boolean;
    salesRepresentative?: number;
  } | null;
  updatedAt: string;
  relationships: {
    ownerships: ClientRelationship[];
    incomeSources: ClientRelationship[];
  };
  isFlowThroughEntity: boolean;
  serviceStartingDate?: string | null;
  serviceEndingDate?: string | null;
  initialFilingDate?: string | null;
  finalFilingDate?: string | null;
};

export function getAllRelationships(
  client: ClientResponse
): ClientRelationship[] {
  if (!client.relationships) {
    return [];
  }

  return [
    ...(client.relationships.ownerships || []),
    ...(client.relationships.incomeSources || []),
  ];
}

export function categorizeRelationships(relationships: ClientRelationship[]): {
  ownerships: ClientRelationship[];
  incomeSources: ClientRelationship[];
} {
  const getCategoryId = (r: ClientRelationship): number | undefined => {
    if (r.clientRelationshipType !== undefined) {
      return r.clientRelationshipType.categoryId;
    }
    if (r.clientRelationshipTypeId !== undefined) {
      return OWNERSHIP_RELATIONSHIP_TYPE_IDS.has(r.clientRelationshipTypeId)
        ? RELATIONSHIP_CATEGORIES.OWNERSHIPS.id
        : RELATIONSHIP_CATEGORIES.INCOME_SOURCES.id;
    }
    return undefined;
  };

  const ownerships = relationships.filter(
    (r) => getCategoryId(r) === RELATIONSHIP_CATEGORIES.OWNERSHIPS.id
  );
  const incomeSources = relationships.filter(
    (r) => getCategoryId(r) === RELATIONSHIP_CATEGORIES.INCOME_SOURCES.id
  );

  return { ownerships, incomeSources };
}

export type CreateClientPayload = {
  id?: number;
  name: string;
  federalLegalId: string;
  accountId: number | null;
  filingCategoryId: number | null;
  isActive: boolean;
  isLocked: boolean;
  businessActivityId: number | null;
  billingTypeId: number | null;
  isClient: boolean;
  dateEstablished: string;
  closeDate: string | null;
  isFormedByUs: boolean;
  additionalInformation: string | null;
  entityType: { id: number; startingDate: string };
  filingStatus: {
    id: number;
    startingDate: string;
    parent: NewClient | number | null;
  };
  domesticState: { id: number; startingDate: string };
  currentParent?: string | undefined;
  isFlowThroughEntity: boolean;
  serviceStartingDate?: string | null;
  serviceEndingDate?: string | null;
  initialFilingDate?: string | null;
  finalFilingDate?: string | null;
  relatedClient?: {
    id: number;
    filesUnderParentCurrentSinceDate: string;
  };
};

export type EditClientPayload = {
  id?: number;
  name: string;
  federalLegalId: string;
  accountId: number | null;
  filingCategoryId: number | null;
  isActive: boolean;
  isLocked: boolean;
  businessActivityId: number | null;
  billingTypeId: number | null;
  isClient: boolean;
  dateEstablished: string;
  closeDate: string | null;
  isFormedByUs: boolean;
  additionalInformation: string | null;
  entityTypes: { id: number; startingDate: string; endingDate?: string }[];
  filingStatuses: { id: number; startingDate: string; endingDate?: string }[];
  states: {
    id: number;
    residenceId: number;
    startingDate: string;
    endingDate?: string;
  }[];
  currentParent?: string | undefined;
};

export const createNewClient = (
  data: Partial<CreateClientPayload>
): Promise<ClientResponse> => {
  return apiClient.post(`/clients`, data);
};

export const validateCreateNewClient = (
  data: Partial<CreateClientPayload>
): Promise<{ isValid: boolean; validationErrors?: string[] }> => {
  return apiClient.post(`/clients/validate-create`, data);
};

export const getAllClients = (params?: {
  includeInactive?: boolean;
  includeNonClient?: boolean;
  search?: string;
}): Promise<{ results: ClientResponse[] }> => {
  return apiClient.get(`/clients`, { ...params });
};

export const getClientById = async (
  id: string | number,
  options?: ApiCallOptions
): Promise<ClientResponse> => {
  return apiClient.get(`/clients/${id}`, {}, options);
};

export const deleteClient = async (id: string): Promise<ClientResponse> => {
  return apiClient.del(`/clients/${id}`);
};

export type UpdateClientPayload = {
  filingStatuses: {
    id: number;
    startingDate: string;
    endingDate?: string;
    description?: string;
  }[];
  entityTypes: {
    id: number;
    startingDate: string;
    endingDate?: string;
    description?: string;
  }[];
  states: {
    id: number;
    startingDate: string;
    endingDate?: string;
    description?: string;
    residenceId: number;
    governmentId?: string;
  }[];
  isLocked: boolean;
  name: string;
  federalLegalId: string;
  accountId?: number;
  filingCategoryId?: number;
  isActive: boolean;
  businessActivityId?: number;
  billingTypeId?: number;
  isClient: boolean;
  dateEstablished: string;
  closeDate?: string;
  isFormedByUs: boolean;
  isFlowThroughEntity: boolean;
  additionalInformation?: string;
  serviceStartingDate?: string | null;
  serviceEndingDate?: string | null;
  initialFilingDate?: string | null;
  finalFilingDate?: string | null;
  contacts?: {
    contact: {
      firstName: string;
      lastName: string;
      dob: string | undefined;
      ssn: string | undefined;
      phone: string | undefined;
      cell: string | undefined;
      fax: string | undefined;
      email: string | undefined;
      ccEmails?: string[];
    };
    contactType?: number;
    contactRoles: number[];
    startingDate?: string;
  }[];
  addresses?: {
    address: {
      street: string;
      unit?: string;
      city?: number;
      state?: number;
      zipCode: number;
    };
    contactType: number;
    contactRoles: number[];
    startingDate?: string;
  }[];
  outsideProviders?: {
    outsideProvider: {
      outsideProviderTypeId: number;
      name: string;
      contact: {
        contactPerson: string;
        phone?: string;
        cell?: string;
        email?: string;
        ccEmails?: string[];
      };
    };
    startingDate: string;
    endingDate?: string;
  }[];
  bankAccounts?: {
    bankAccount: {
      accountNumber: string;
      routingNumber: string;
      description: string;
      isDefaultPaymentMethod: boolean;
    };
  }[];
  creditCards?: {
    creditCard: {
      accountNumber: string;
      nameOnAccount: string;
      expirationDate: string;
      securityCode: string;
      address: string;
      description: string;
      isDefaultPaymentMethod: boolean;
    };
  }[];
  commissionDetails?: {
    isSubjectToCommission?: boolean;
    salesRepresentative?: number;
    commissionRate?: number;
  };
  relationships?: {
    id?: number;
    relationshipClient?: number | NewClient | EditedClient | null;
    clientRelationshipTypeId?: number;
    startingDate: string;
    endingDate?: string;
    isFinalRelationship?: boolean;
    areJointSpouses: boolean;
    profit: number;
    loss: number;
    capital: number;
    clientRelationshipActivityTypeId?: number;
    isActive?: boolean;
    k1Applicable: boolean;
    k1Provider?: number;
    quickReminder?: string;
  }[];
  templates?: {
    templateInfo: {
      templateId: number;
      templateRecurrenceId: number;
      startingDate: string;
      endingDate?: string;
      isFinal?: boolean;
      defaultStaffId?: number;
      quickReminder?: string;
      taxPayment?: { id: number; name: string } | null;
      mainFiling?: { id: number; name: string } | null;
    };
    billingAndPayments: {
      isSeparatelyCharged: boolean;
      amount?: number;
      estimateAmount?: number;
      isSubjectToCommission: boolean;
      commissionRate?: number;
      priceStatus?: number;
      isPriceChanged: boolean;
      newPrice?: number;
      paymentType?: number;
      dayOfMonth?: number;
      defaultPaymentMethod?:
        | { bankAccount: Omit<ClientResponse['bankAccounts'][number], 'id'> }
        | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> }
        | null;
      customerContact?: Exclude<
        UpdateClientPayload['contacts'],
        undefined
      >[number];
    };
    recurrenceOverride?: {
      firstMonth?: number;
      secondMonth?: number;
      thirdMonth?: number;
      fourthMonth?: number;
      dayOfMonth?: number;
    };
    assignments?: {
      id?: number;
      templateAssignmentId?: number;
      name: string;
      defaultStaffId?: number;
      assignmentTypeId: number;
      order: number;
      isRequired: boolean;
      canBeDeleted: boolean;
      isActive: boolean;
      isFinal: boolean;
      amount?: number;
    }[];
    states?: {
      id?: number;
      templateStateId: number;
      clientState:
        | Exclude<UpdateClientPayload['states'], undefined>[number]
        | number
        | null;
      name: string;
      form: string | null;
      state: {
        id: number;
        name: string;
      };
      startingDate: string | null;
      endingDate: string | undefined;
      isFinal?: boolean;
      quickReminder: string | undefined;
      paymentMethod?:
        | { bankAccount: Omit<ClientResponse['bankAccounts'][number], 'id'> }
        | { creditCard: Omit<ClientResponse['creditCards'][number], 'id'> };
      totalUsedTasks: number;
    }[];
  }[];
};

export function updateClient(
  id: number,
  payload: UpdateClientPayload
): Promise<ClientResponse> {
  return apiClient.put(`/clients/${id}`, payload);
}

export function validateUpdateClient(
  id: number,
  payload: UpdateClientPayload
): Promise<{ isValid: boolean; validationErrors?: string[] }> {
  return apiClient.post(`clients/${id}/validate-update`, payload);
}

export type ValidatePaymentMethodsPayload = {
  clientId: number;
  bankAccounts?: {
    bankAccount: {
      accountNumber: string;
      routingNumber: string;
      description: string;
      isDefaultPaymentMethod: boolean;
    };
  }[];
  creditCards?: {
    creditCard: {
      accountNumber: string;
      nameOnAccount: string;
      expirationDate?: string;
      securityCode: string;
      address: string;
      description: string;
      isDefaultPaymentMethod: boolean;
    };
  }[];
};

export const validatePaymentMethods = (
  clientId: number,
  payload: ValidatePaymentMethodsPayload
): Promise<{ isValid: boolean; validationErrors?: string[] }> => {
  return apiClient.post(
    `clients/${clientId}/validate-payment-methods`,
    payload
  );
};

const SORTING_TYPES = {
  ascending: 'ASC',
  descending: 'DESC',
} as const;

export const getClients = (params?: {
  pageNumber: number;
  pageSize: number;
  search: string;
  sortBy?: string;
  sortType?: (typeof SORTING_TYPES)[keyof typeof SORTING_TYPES];
  includeInactive?: boolean;
  includeNonClient?: boolean;
}): Promise<{
  results: ClientResponse[];
  totalCount: number;
}> => {
  return apiClient.get(`/clients`, { ...params });
};

export function mapContactFromResponseToContactPayload(
  contact: ClientResponse['contacts'][number]
): Exclude<UpdateClientPayload['contacts'], undefined>[number] {
  return {
    contact: {
      firstName: contact.firstName,
      lastName: contact.lastName,
      dob: contact.dob || undefined,
      ssn: contact.ssn || undefined,
      phone: contact.phone || undefined,
      cell: contact.cell || undefined,
      fax: contact.fax || undefined,
      email: contact.email || undefined,
      ccEmails: contact.ccEmails.length ? contact.ccEmails : undefined,
    },
    contactType: contact.contactTypeId,
    contactRoles: contact.contactRoles,
    startingDate: contact.startingDate || undefined,
  };
}

export function mapBankAccountFromResponseToPayload(
  bankAccount: ClientResponse['bankAccounts'][number]
): Exclude<UpdateClientPayload['bankAccounts'], undefined>[number] {
  return {
    bankAccount: {
      accountNumber: bankAccount.accountNumber,
      routingNumber: bankAccount.routingNumber,
      description: bankAccount.description,
      isDefaultPaymentMethod: bankAccount.isDefaultPaymentMethod,
    },
  };
}

export function mapCreditCardFromResponseToPayload(
  creditCard: ClientResponse['creditCards'][number]
): Exclude<UpdateClientPayload['creditCards'], undefined>[number] {
  return {
    creditCard: {
      accountNumber: creditCard.accountNumber,
      nameOnAccount: creditCard.nameOnAccount,
      expirationDate: creditCard.expirationDate,
      securityCode: creditCard.securityCode,
      address: creditCard.address,
      description: creditCard.description,
      isDefaultPaymentMethod: creditCard.isDefaultPaymentMethod,
    },
  };
}

export function mapStateFromResponseToPayload(
  state: ClientResponse['stateHistory'][number]
): UpdateClientPayload['states'][number] {
  return {
    id: state.stateId,
    startingDate: state.startingDate,
    endingDate: state.endingDate || undefined,
    description: state.description ?? undefined,
    residenceId: state.stateResidenceId,
    governmentId: state.governmentId ?? undefined,
  };
}

export function remapClientResponseToClientPayload(
  client: ClientResponse
): UpdateClientPayload {
  const payload: UpdateClientPayload = {
    filingStatuses: client.filingStatusHistory?.map((e) => ({
      id: e.filingStatusId,
      startingDate: e.startingDate,
      endingDate: e.endingDate || undefined,
      description: e.description ?? undefined,
    })),
    entityTypes: client.entityTypeHistory?.map((e) => ({
      id: e.entityTypeId,
      startingDate: e.startingDate,
      endingDate: e.endingDate || undefined,
      description: e.description ?? undefined,
    })),
    states: client.stateHistory?.map((e) => mapStateFromResponseToPayload(e)),
    isLocked: client.isLocked ?? false,
    name: client.name,
    federalLegalId: formatFed(
      client.federalLegalId,
      client.entityType.name === 'Individual'
        ? FedTypes.Individual
        : FedTypes.Other
    ),
    accountId: client.accountId?.id,
    filingCategoryId: client.filingCategory?.id,
    isActive: client.isActive,
    businessActivityId: client.businessActivity?.id,
    billingTypeId: client.billingType?.id,
    isClient: client.isClient,
    isFlowThroughEntity: client.isFlowThroughEntity || false,
    serviceStartingDate: client.serviceStartingDate || undefined,
    serviceEndingDate: client.serviceEndingDate || undefined,
    initialFilingDate: client.initialFilingDate || undefined,
    finalFilingDate: client.finalFilingDate || undefined,
    dateEstablished: client.dateEstablished,
    closeDate: client.closeDate || undefined,
    isFormedByUs: client.isFormedByUs,
    additionalInformation: client.additionalInformation,
    contacts: client.contacts.length
      ? client.contacts.map((contact) =>
          mapContactFromResponseToContactPayload(contact)
        )
      : undefined,
    addresses: client.addresses.length
      ? client.addresses.map((address) => ({
          address: {
            street: address.street,
            city: address.city?.id,
            state: address.state?.id,
            zipCode: address.zipCode.id,
          },
          contactType: address.contactTypeId,
          contactRoles: address.contactRoles,
          startingDate: address.startingDate || undefined,
        }))
      : undefined,
    outsideProviders: client.outsideProviders.length
      ? client.outsideProviders.map((provider) => ({
          outsideProvider: {
            outsideProviderTypeId: provider.type.id,
            name: provider.name,
            contact: {
              contactPerson: provider.contact.contactPerson,
              phone: provider.contact.phone || undefined,
              cell: provider.contact.cell || undefined,
              email: provider.contact.email || undefined,
              ccEmails: provider.contact.ccEmails?.length
                ? provider.contact.ccEmails
                : undefined,
            },
          },
          startingDate: provider.startingDate,
          endingDate: provider.endingDate || undefined,
        }))
      : undefined,
    bankAccounts: client.bankAccounts.length
      ? client.bankAccounts.map((b) => mapBankAccountFromResponseToPayload(b))
      : undefined,
    creditCards: client.creditCards.length
      ? client.creditCards.map((c) => mapCreditCardFromResponseToPayload(c))
      : undefined,
    commissionDetails: client.commissionDetails?.isSubjectToCommission
      ? {
          isSubjectToCommission: client.commissionDetails.isSubjectToCommission,
          salesRepresentative:
            client.commissionDetails.salesRepresentative ?? undefined,
          commissionRate: client.commissionDetails.commissionRate,
        }
      : undefined,
    templates: client.templates.length
      ? client.templates.map((t) => {
          const defaultPaymentMethod = t.billingAndPayments.defaultPaymentMethod
            ? 'bank' in t.billingAndPayments.defaultPaymentMethod
              ? {
                  bankAccount: t.billingAndPayments.defaultPaymentMethod.bank,
                }
              : {
                  creditCard:
                    t.billingAndPayments.defaultPaymentMethod.creditCard,
                }
            : undefined;

          const selectedContact =
            typeof t.billingAndPayments.customerContact === 'number'
              ? client.contacts.find(
                  (c) => c.id === t.billingAndPayments.customerContact
                )
              : t.billingAndPayments.customerContact;

          const recurrenceOverride: Exclude<
            UpdateClientPayload['templates'],
            undefined
          >[number]['recurrenceOverride'] =
            t.recurrence.recurrenceOverride || undefined;

          const assignments: Exclude<
            UpdateClientPayload['templates'],
            undefined
          >[number]['assignments'] = t.assignments.length
            ? t.assignments.map((a, index) => ({
                ...a,
                amount: a.amount ?? undefined,
                isFinal: resolveIsFinalAssignment(a),
                id: a.id > 0 ? a.id : undefined,
                templateAssignmentId:
                  a.templateAssignmentId && a.templateAssignmentId > 0
                    ? a.templateAssignmentId
                    : undefined,
                defaultStaffId: a.defaultStaffId || undefined,
                order: index + 1,
              }))
            : undefined;
          const states: Exclude<
            UpdateClientPayload['templates'],
            undefined
          >[number]['states'] = [
            ...(t.states?.added || []),
            ...(t.states?.outOfRange || []),
          ].map((s) => {
            const paymentMethod = s.paymentMethod
              ? 'bank' in s.paymentMethod
                ? {
                    bankAccount: s.paymentMethod.bank,
                  }
                : {
                    creditCard: s.paymentMethod.creditCard,
                  }
              : undefined;

            return {
              id: s.id,
              templateStateId: s.templateStateId,
              clientState:
                typeof s.clientStateId === 'object'
                  ? mapStateFromResponseToPayload(s.clientStateId)
                  : s.clientStateId,
              name: s.name,
              form: s.form || null,
              state: s.state,
              startingDate: s.startingDate,
              endingDate: s.endingDate || undefined,
              isFinal: s.isFinal,
              quickReminder: s.quickReminder || undefined,
              paymentMethod:
                s.paymentMethod && 'name' in s.paymentMethod
                  ? undefined
                  : paymentMethod,
              totalUsedTasks: 0,
            };
          });
          return {
            templateInfo: {
              templateId: t.templateInfo.templateId,
              templateRecurrenceId: t.templateInfo.templateRecurrenceId,
              startingDate: t.templateInfo.startingDate,
              endingDate: t.templateInfo.endingDate || undefined,
              isFinal: t.templateInfo.isFinal ?? undefined,
              defaultStaffId: t.templateInfo.defaultStaffId || undefined,
              quickReminder: t.templateInfo.quickReminder || undefined,
              taxPayment: t.templateInfo.taxPayment,
              mainFiling: t.templateInfo.mainFiling,
            },
            billingAndPayments: {
              isSeparatelyCharged:
                t.billingAndPayments.isSeparatelyCharged ?? false,
              amount: t.billingAndPayments.amount || undefined,
              estimateAmount: t.billingAndPayments.estimateAmount || undefined,
              isSubjectToCommission:
                t.billingAndPayments.isSubjectToCommission || false,
              commissionRate: t.billingAndPayments.commissionRate ?? undefined,
              priceStatus: t.billingAndPayments.priceStatus || undefined,
              isPriceChanged: t.billingAndPayments.isPriceChanged,
              newPrice: t.billingAndPayments.newPrice || undefined,
              paymentType: t.billingAndPayments.paymentType || undefined,
              dayOfMonth: t.billingAndPayments.dayOfMonth || undefined,
              defaultPaymentMethod: defaultPaymentMethod,
              customerContact: selectedContact
                ? mapContactFromResponseToContactPayload(selectedContact)
                : undefined,
            },
            recurrenceOverride: recurrenceOverride,
            assignments: assignments,
            states: states?.length ? states : undefined,
          };
        })
      : undefined,
  };

  const validRelationships = getAllRelationships(client)
    .filter((relationship) => {
      const typeId =
        relationship.clientRelationshipType?.id ||
        relationship.clientRelationshipTypeId;
      const hasClient =
        relationship.relationshipClient || relationship.relationshipClientId;
      return (
        (typeId ?? 0) > 0 && hasClient && Boolean(relationship.startingDate)
      );
    })
    .map((relationship) => {
      const relClient = relationship.relationshipClient;

      const getRelationshipClient = (): number | NewClient | EditedClient => {
        if (
          relClient &&
          typeof relClient === 'object' &&
          'originalGlobalClientId' in relClient &&
          relClient.originalGlobalClientId
        ) {
          return {
            clientId: relClient.originalGlobalClientId,
            clientData: {
              clientInfo: relClient.clientInfo,
              contact: relClient.contact,
              address: relClient.address,
            },
          };
        }

        if (
          relClient &&
          typeof relClient === 'object' &&
          'clientInfo' in relClient
        ) {
          return relClient;
        }

        if (relClient && typeof relClient === 'object' && 'id' in relClient) {
          return relClient.id;
        }

        if (relClient) {
          return relClient as number;
        }

        return relationship.relationshipClientId!;
      };

      const relationshipClient = getRelationshipClient();

      const clientRelationshipTypeId =
        relationship.clientRelationshipType?.id ||
        relationship.clientRelationshipTypeId!;

      return {
        id: relationship.id,
        relationshipClient,
        clientRelationshipTypeId,
        startingDate: relationship.startingDate,
        endingDate: relationship.endingDate || undefined,
        isFinalRelationship: relationship.isFinalRelationship || undefined,
        areJointSpouses: relationship.areJointSpouses,
        profit: relationship.profit,
        loss: relationship.loss,
        capital: relationship.capital,
        clientRelationshipActivityTypeId:
          relationship.activityType?.id || undefined,
        isActive: relationship.isActive || undefined,
        k1Applicable: relationship.k1Applicable ?? false,
        k1Provider: relationship.k1Provider || undefined,
        quickReminder: relationship.quickReminder || undefined,
      };
    });

  if (validRelationships.length > 0) {
    payload.relationships = validRelationships;
  }

  return payload;
}

export type PriceStatusesResponse = {
  id: number;
  name: string;
}[];

export type PaymentTypesResponse = {
  id: number;
  name: string;
}[];

export const getPriceStatuses = (): Promise<PriceStatusesResponse> => {
  return apiClient.get(`/clients/client-templates/options/price-status`, {});
};
export const getPaymentTypes = (): Promise<PaymentTypesResponse> => {
  return apiClient.get(`/clients/client-templates/options/payment-type`, {});
};
